import React, { useEffect, useState } from 'react';
import { Button, Image, Layout, message, Modal, Space, Table, Spin, Row, Col, Upload } from 'antd';
import { EditOutlined, DeleteOutlined, UploadOutlined, PlusOutlined } from '@ant-design/icons';
import {
    getAllHappyCustomers,
    createHappyCustomer,
    updateHappyCustomer,
    deleteHappyCustomer
} from '../../api/happyCustomer';
import { Content } from 'antd/es/layout/layout';

const { Header } = Layout;

const HappyCustomers = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [uploading, setUploading] = useState(false);

    // Fetch all happy customers
    const fetchHappyCustomers = async () => {
        setLoading(true);
        try {
            const response = await getAllHappyCustomers();
            setData(response.data);
        } catch (error) {
            message.error('Failed to fetch happy customers');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchHappyCustomers();
    }, []);

    // Handle create modal
    const handleCreate = () => {
        setFileList([]);
        setIsCreateModalVisible(true);
    };

    // Handle edit modal
    const handleEdit = (record) => {
        setEditingRecord(record);
        setFileList([]);
        setIsEditModalVisible(true);
    };

    // Handle delete
    const handleDelete = (record) => {
        Modal.confirm({
            title: 'Are you sure you want to delete this entry?',
            content: 'All images will be permanently deleted.',
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    await deleteHappyCustomer(record._id);
                    message.success('Entry deleted successfully');
                    fetchHappyCustomers();
                } catch (error) {
                    message.error('Failed to delete entry');
                }
            },
        });
    };

    // Handle file upload validation
    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg';
        if (!isJpgOrPng) {
            message.error('You can only upload JPG/PNG files!');
            return Upload.LIST_IGNORE;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Image must be smaller than 5MB!');
            return Upload.LIST_IGNORE;
        }
        return false; // Prevent automatic upload
    };

    // Handle file list change
    const handleChange = ({ fileList: newFileList }) => {
        // Limit to 10 images
        if (newFileList.length > 10) {
            message.warning('You can only upload a maximum of 10 images');
            return;
        }
        setFileList(newFileList);
    };

    // Handle create submit
    const handleCreateSubmit = async () => {
        if (fileList.length === 0) {
            message.warning('Please upload at least one image');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            fileList.forEach((file) => {
                formData.append('images', file.originFileObj);
            });

            await createHappyCustomer(formData);
            message.success('Images uploaded successfully!');
            setIsCreateModalVisible(false);
            setFileList([]);
            fetchHappyCustomers();
        } catch (error) {
            message.error('Failed to upload images');
        } finally {
            setUploading(false);
        }
    };

    // Handle edit submit
    const handleEditSubmit = async () => {
        if (fileList.length === 0) {
            message.warning('Please upload at least one image');
            return;
        }

        setUploading(true);
        try {
            const formData = new FormData();
            fileList.forEach((file) => {
                formData.append('images', file.originFileObj);
            });

            await updateHappyCustomer(editingRecord._id, formData);
            message.success('Images updated successfully!');
            setIsEditModalVisible(false);
            setFileList([]);
            setEditingRecord(null);
            fetchHappyCustomers();
        } catch (error) {
            message.error('Failed to update images');
        } finally {
            setUploading(false);
        }
    };

    const columns = [
        {
            title: 'S.No',
            key: 'index',
            width: 80,
            render: (_, __, index) => index + 1,
        },
        {
            title: 'Images',
            dataIndex: 'images',
            key: 'images',
            width: 600,
            render: (images) => (
                <Space size={8} wrap>
                    {images?.map((url, index) => (
                        <Image
                            key={index}
                            src={url}
                            width={80}
                            height={80}
                            style={{ objectFit: 'cover', borderRadius: 4 }}
                        />
                    ))}
                </Space>
            ),
        },
        {
            title: 'Total Images',
            dataIndex: 'images',
            key: 'count',
            width: 120,
            render: (images) => images?.length || 0,
        },
        {
            title: 'Created At',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 200,
            render: (date) => new Date(date).toLocaleDateString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            }),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 150,
            render: (_, record) => (
                <Space>
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        Edit
                    </Button>
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        Delete
                    </Button>
                </Space>
            ),
            fixed: 'right'
        },
    ];

    return (
        <Layout style={{ height: '100vh', overflow: 'hidden' }}>
            <Header style={{ background: '#fff', padding: 0 }}>
                <Row justify="space-between" align="middle" style={{ padding: '0 24px' }}>
                    <Col>
                        <h2>Our Happy Customers</h2>
                    </Col>
                    <Col>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            Add Images
                        </Button>
                    </Col>
                </Row>
            </Header>
            <Content style={{ margin: '24px 16px 0' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        dataSource={data}
                        rowKey={(record) => record._id}
                        pagination={{ position: ['bottomCenter'], pageSize: 10 }}
                        scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
                        style={{ marginTop: 16 }}
                    />
                )}
            </Content>

            {/* Create Modal */}
            <Modal
                title="Add Happy Customer Images"
                visible={isCreateModalVisible}
                onCancel={() => {
                    setIsCreateModalVisible(false);
                    setFileList([]);
                }}
                onOk={handleCreateSubmit}
                okText="Upload"
                confirmLoading={uploading}
                width={700}
            >
                <div style={{ marginBottom: 16 }}>
                    <p>Upload images (JPG/PNG only, max 10 images)</p>
                </div>
                <Upload
                    listType="picture-card"
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onChange={handleChange}
                    multiple
                    maxCount={10}
                >
                    {fileList.length >= 10 ? null : (
                        <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                        </div>
                    )}
                </Upload>
            </Modal>

            {/* Edit Modal */}
            <Modal
                title="Edit Happy Customer Images"
                visible={isEditModalVisible}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setFileList([]);
                    setEditingRecord(null);
                }}
                onOk={handleEditSubmit}
                okText="Update"
                confirmLoading={uploading}
                width={700}
            >
                <div style={{ marginBottom: 16 }}>
                    <p><strong>Current Images:</strong></p>
                    <Space size={8} wrap>
                        {editingRecord?.images?.map((url, index) => (
                            <Image
                                key={index}
                                src={url}
                                width={80}
                                height={80}
                                style={{ objectFit: 'cover', borderRadius: 4 }}
                            />
                        ))}
                    </Space>
                </div>
                <div style={{ marginTop: 16, marginBottom: 16 }}>
                    <p><strong>Upload New Images (will replace all existing images):</strong></p>
                    <p>Upload images (JPG/PNG only, max 10 images)</p>
                </div>
                <Upload
                    listType="picture-card"
                    fileList={fileList}
                    beforeUpload={beforeUpload}
                    onChange={handleChange}
                    multiple
                    maxCount={10}
                >
                    {fileList.length >= 10 ? null : (
                        <div>
                            <PlusOutlined />
                            <div style={{ marginTop: 8 }}>Upload</div>
                        </div>
                    )}
                </Upload>
            </Modal>
        </Layout>
    );
};

export default HappyCustomers;
